import { useEffect, useRef, useState } from 'react';
import { Camera, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { resizeImage } from '@/lib/image';

interface PhotoCaptureProps {
  label?: string;
  onCapture: (blob: Blob) => Promise<void> | void;
  /** Current captured blob (controlled). When set, its preview is shown. */
  value?: Blob | null;
  /** Clear the captured value. When provided, the remove button calls this. */
  onRemove?: () => void;
  maxDimension?: number;
}

const DEFAULT_MAX = 1600;

/**
 * Single-image capture (used for BOL / POD documents). Shows a preview of the
 * captured document and allows replacing or removing it. For multi-photo
 * capture use {@link BillPhotos} instead.
 */
export function PhotoCapture({ label = 'Take photo', onCapture, value, onRemove, maxDimension = DEFAULT_MAX }: PhotoCaptureProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!value) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(value);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const compressed = await resizeImage(file, maxDimension);
      await onCapture(compressed);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onChange}
        className="hidden"
      />
      {previewUrl && (
        <div className="relative">
          <img src={previewUrl} alt="captured" className="w-full rounded-card object-cover" />
          {onRemove && (
            <button
              type="button"
              aria-label="Remove photo"
              onClick={onRemove}
              className="absolute right-2 top-2 rounded-full bg-brand-error p-2 text-white shadow"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
      <Button
        variant="outline"
        fullWidth
        size="lg"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
      >
        <Camera className="mr-2 h-5 w-5" />
        {busy ? 'Processing...' : previewUrl ? `Replace ${label.toLowerCase()}` : label}
      </Button>
    </div>
  );
}
